/**
 * TanStack Query hooks for authentication
 * Handles async operations with proper caching and error handling
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, getStoredToken } from '../stores/authStore';
import { 
  loginUser, 
  registerUser, 
  logoutUser, 
  getUserProfile, 
  refreshUserToken 
} from '../api/auth';
import { usePostHogAnalytics } from '../hooks/usePostHogAnalytics';
import { PostHogEvents } from '../utils/posthogEvents';

// Query keys
export const authQueryKeys = {
  all: ['auth'] as const,
  profile: () => [...authQueryKeys.all, 'profile'] as const,
  session: () => [...authQueryKeys.all, 'session'] as const,
};

/**
 * Login mutation - handles login flow with no race conditions
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToken, setUser, updateSessionTracking, setError } = useAuthStore();
  const { trackAuth, identifyUser } = usePostHogAnalytics();
  
  return useMutation({
    mutationFn: async ({ 
      usernameOrEmail, 
      password 
    }: { 
      usernameOrEmail: string; 
      password: string;
    }) => {
      const response = await loginUser(usernameOrEmail, password);
      return { response, usernameOrEmail };
    },
    
    onSuccess: async ({ response, usernameOrEmail }) => {
      const token = response.access_token;
      
      // 1. Set token in Zustand (synchronous!)
      setToken(token);
      updateSessionTracking();
      
      // 2. Track login immediately - no race condition!
      console.log('🎯 Tracking login with Zustand - no race condition');
      trackAuth(PostHogEvents.USER_LOGGED_IN, {
        login_method: 'email',
        username_or_email: usernameOrEmail,
        login_timestamp: new Date().toISOString(),
        login_count: useAuthStore.getState().loginCount
      });
      
      // 3. Invalidate profile query to fetch fresh data
      await queryClient.invalidateQueries({ queryKey: authQueryKeys.profile() });
      
      // 4. Navigate based on profile status
      const profile = queryClient.getQueryData(authQueryKeys.profile());
      if (profile) {
        const user = profile as any;
        
        // Track with user details
        identifyUser(user.id.toString(), {
          email: user.email,
          username: user.username,
          profile_exists: user.profile_exists,
          email_verified: user.isVerified
        });
        
        if (!user.isVerified) {
          navigate('/verify-email', { state: { email: user.email } });
        } else if (!user.profile_exists) {
          navigate('/onboarding');
        }
      }
    },
    
    onError: (error: any) => {
      console.error('Login failed:', error);
      setError(error.message || 'Login failed');
      
      trackAuth(PostHogEvents.USER_LOGIN_FAILED, {
        login_method: 'email',
        error_message: error.message
      });
    }
  });
};

/**
 * User profile query - fetches and caches user profile
 */
export const useUserProfile = () => {
  const token = useAuthStore((state) => state.token);
  const { setUser } = useAuthStore();
  
  return useQuery({
    queryKey: authQueryKeys.profile(),
    queryFn: async () => {
      // Use token from store - always up to date!
      const currentToken = getStoredToken();
      if (!currentToken) {
        throw new Error('No authentication token');
      }
      
      const response = await getUserProfile(currentToken);
      const userData = response.user || response;
      
      const user = {
        id: userData.user_id || userData.id,
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        birthday: userData.birthday,
        isVerified: userData.email_verified !== undefined ? userData.email_verified : false,
        avatar: userData.avatar || userData.profile?.profile_settings?.avatar_url,
        tagline: userData.profile?.profile_settings?.tagline,
        profile_exists: userData.profile_exists,
        is_superuser: userData.is_superuser,
      };
      
      // Update Zustand store
      setUser(user);
      
      return user;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    }
  });
};

/**
 * Logout mutation
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { reset, getToken } = useAuthStore();
  const { trackAuth } = usePostHogAnalytics();
  
  return useMutation({
    mutationFn: async () => {
      const token = getToken();
      if (token) {
        await logoutUser(token);
      }
    },
    
    onSuccess: () => {
      // Track logout
      trackAuth(PostHogEvents.USER_LOGGED_OUT, {
        user_journey_stage: 'session_end',
        logout_method: 'user_initiated'
      });
      
      // Clear Zustand store
      reset();
      
      // Clear React Query cache
      queryClient.clear();
      
      // Navigate to login
      navigate('/login');
    },
    
    onError: (error) => {
      console.error('Logout error:', error);
      // Even if API fails, clear local state
      reset();
      queryClient.clear();
      navigate('/login');
    }
  });
};

/**
 * Register mutation
 */
export const useRegister = () => {
  const navigate = useNavigate();
  const { trackAuth } = usePostHogAnalytics();
  
  return useMutation({
    mutationFn: async ({
      email,
      username,
      password
    }: {
      email: string;
      username: string;
      password: string;
    }) => {
      const response = await registerUser(email, username, password);
      return { response, email, username, password };
    },
    
    onSuccess: async ({ response, email, username, password }) => {
      console.log('Registration successful:', response);
      
      // Track signup
      trackAuth(PostHogEvents.USER_SIGNED_UP, {
        signup_method: 'email',
        user_journey_stage: 'new',
        signup_timestamp: new Date().toISOString()
      });
      
      // Auto-login after registration
      try {
        const loginResponse = await loginUser(username, password);
        const token = loginResponse.access_token;
        
        if (token) {
          useAuthStore.getState().setToken(token);
          useAuthStore.getState().updateSessionTracking();
          
          trackAuth(PostHogEvents.USER_LOGGED_IN, {
            login_method: 'auto_after_signup',
            auto_logged_in: true
          });
        }
      } catch (error) {
        console.log('Auto-login failed, but registration successful');
      }
      
      // Navigate to email verification
      navigate('/verify-email', { state: { email } });
    },
    
    onError: (error: any) => {
      console.error('Registration failed:', error);
      
      trackAuth(PostHogEvents.USER_SIGNUP_FAILED, {
        signup_method: 'email',
        error_message: error.message
      });
    }
  });
};

/**
 * Token refresh mutation
 */
export const useRefreshToken = () => {
  const { setToken, getToken } = useAuthStore();
  const { trackAuth } = usePostHogAnalytics();
  
  return useMutation({
    mutationFn: async () => {
      const currentToken = getToken();
      if (!currentToken) {
        throw new Error('No token to refresh');
      }
      
      const response = await refreshUserToken(currentToken);
      return response.access_token;
    },
    
    onSuccess: (newToken) => {
      setToken(newToken);
      
      trackAuth(PostHogEvents.TOKEN_REFRESHED, {
        user_journey_stage: 'session_extended',
        refresh_method: 'automatic'
      });
    },
    
    onError: (error) => {
      console.error('Token refresh failed:', error);
      // Don't clear auth on refresh failure - allow retry
    }
  });
};

/**
 * Hook to check if user is authenticated
 * Uses Zustand for synchronous access - no race conditions!
 */
export const useIsAuthenticated = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  
  return {
    isAuthenticated,
    hasToken: !!token
  };
};